"use client"

import type { MouseEvent, ReactNode } from "react"

interface AnchorNavLinkProps {
  /** Target element id, without the leading "#". */
  anchor: string
  id?: string
  className?: string
  /** Runs before the scroll, e.g. to close the mobile menu. */
  onNavigate?: () => void
  children: ReactNode
}

/** Frames to wait for the body scroll lock before scrolling anyway. */
const MAX_UNLOCK_FRAMES = 30

/**
 * The mobile menu locks the body (`position: fixed`, see `setBodyOverflow`) and
 * releases it a tick after `onNavigate`. A scroll issued before the release does
 * nothing, so hold the scroll until the body accepts it again.
 */
const whenBodyScrolls = (run: () => void, frame = 0) => {
  if (document.body.style.position !== "fixed" || frame >= MAX_UNLOCK_FRAMES) {
    run()
    return
  }
  requestAnimationFrame(() => whenBodyScrolls(run, frame + 1))
}

/**
 * In-page anchor link for the header navigation.
 *
 * The App Router drops a navigation whose target URL equals the current one, so
 * a `next/link` anchor scrolls on the first click and never again: the hash it
 * writes to the address bar makes every later click a no-op, and a shared deep
 * link is dead on arrival. A plain `<a>` plus an explicit scroll runs on every
 * click, whatever the current hash is.
 */
export const AnchorNavLink = ({
  anchor,
  id,
  className,
  onNavigate,
  children,
}: AnchorNavLinkProps) => {
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    // Leave "open in a new tab" and non-primary buttons to the browser.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

    const target = document.getElementById(anchor)
    if (!target) {
      // Nothing to scroll to, so hand the click back to the browser. Close the
      // menu first: leaving the overlay up reads as a dead click on mobile.
      onNavigate?.()
      return
    }

    event.preventDefault()
    // replaceState, not pushState: the URL stays shareable and Back still
    // leaves the page instead of stepping through one entry per click.
    window.history.replaceState(null, "", `#${anchor}`)
    onNavigate?.()

    whenBodyScrolls(() =>
      target.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
      })
    )
  }

  return (
    <a href={`#${anchor}`} id={id} className={className} onClick={handleClick}>
      {children}
    </a>
  )
}
